import { Badge } from 'components/Badge/Badge'
import { FilterEnum, IFilter } from 'components/Filter/SingleFilter'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Pagination } from 'components/Pagination/Pagination'
import { clearIncidents, getIncidents } from 'features/operations/incidentsSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { Button, Flex, H1WithBack, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { IGetAllIncidentsSingleResponse } from 'types/api/operationsIncidentsApi.interface'
import {
    INCIDENT_STATUS_LABELS,
} from 'types/incidents.interface'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import {
    incidentSeverityOptions,
    incidentStatusOptions,
    severityLevelToLabel,
    statusLevelToIncidentStatus,
} from './incidentsData'
import { RaiseIncidentModal } from './RaiseIncidentModal'

const formatLocation = (item: IGetAllIncidentsSingleResponse) => {
    if (!item.location?.name) return '-'
    if (item.cascadeAppliedAt) return `${item.location.name} - cascade applied`
    return item.location.name
}

const columns: IHonestTableColumn<IGetAllIncidentsSingleResponse>[] = [
    {
        id: 'severity',
        header: 'Severity',
        sticky: true,
        minWidth: '4rem',
        cell: (item) => {
            const severity = severityLevelToLabel(item.severityLevel)
            return (
                <Badge text={severity} red={severity === 'L4'} orange={severity !== 'L4'} />
            )
        },
    },
    {
        id: 'timestamp',
        header: 'Time',
        minWidth: '10rem',
        cell: (item) => <OtherText grey>{formatDateForDisplay(item.createdAt, true)}</OtherText>,
    },
    {
        id: 'incident',
        header: 'Incident',
        minWidth: '18rem',
        cell: (item) => (
            <Cell>
                <BodyText weight='600'>{item.title}</BodyText>
                <OtherText grey>{formatLocation(item)}</OtherText>
            </Cell>
        ),
    },
    {
        id: 'spacer',
        header: '',
        fill: true,
        cell: () => null,
    },
    {
        id: 'status',
        header: 'Status',
        align: 'right',
        minWidth: '10rem',
        cell: (item) => {
            const status = statusLevelToIncidentStatus(item.status)
            return (
                <RightCell>
                    <Badge text={INCIDENT_STATUS_LABELS[status]} green={status === 'acknowledged'} orange={status !== 'acknowledged'} />
                </RightCell>
            )
        },
    },
]

const filters: IFilter[] = [
    {
        type: FilterEnum.SELECT,
        name: 'Severity',
        options: incidentSeverityOptions,
        propName: 'severityLevel',
        single: true,
    },
    {
        type: FilterEnum.SELECT,
        name: 'Status',
        options: incidentStatusOptions,
        propName: 'status',
        single: true,
    },
]

export const Incidents = () => {
    const [modalOpen, setModalOpen] = useState(false)
    const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
    const { data, totalPages } = useSelector((state: RootState) => state.incidents)

    useQueryPagination({
        fetchFunctions: [getIncidents],
        cleanUpFunctions: [clearIncidents],
        params,
    })

    return (
        <>
            <NavBar
                enableTemporaryManualClosing
                filterOptions={filters}
                params={params}
                setParams={setParams}
                limit={30}
                visibleFilterOverflow
            />
            <PageWrapper>
                <Flex between width='100%' mb='16'>
                    <H1WithBack title='Incidents' arrow />
                    <Button orange onClick={() => setModalOpen(true)}>
                        Raise Incident
                    </Button>
                </Flex>
                {data?.length ? (
                    <HonestTable columns={columns} data={data} keyField='id' />
                ) : (
                    <NoData text='No incidents to display' />
                )}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
            {modalOpen && (
                <RaiseIncidentModal
                    closeModal={() => setModalOpen(false)}
                    params={params}
                />
            )}
        </>
    )
}
